import { connect } from 'react-redux';
import { map, isEqual } from 'lodash/fp';
import { createStructuredSelector } from 'reselect';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Card } from '@mews-commander/ui';
import { ResponsiveTable, TableContainerElement } from '@mews-commander/ui/legacy';
import { CardTitleElement } from '../../style';
import { getCategoriesOrdering } from '../../../selectors/categories';
import ExportConfigurationButtons from '../ExportConfigurationButtons';
import { getResources, getTotalResourcesCount } from '../../../selectors/resources';
import { getFloors } from '../../../selectors/floors';
import { SimpleResources } from '../../../types';
import ResourceOverviewCategory from './ResourceOverviewCategory';
import ResourceGridCell from './ResourceGridCell';
import ResourceOverviewActionButtons from './ResourceOverviewActionButtons';
import { TableMapping, TableSource, Floor } from './types';
import { ResourceGridCellWrapper } from './style';

interface StateProps {
    orderedCategoryIds: string[];
    floors: number[];
    resources: SimpleResources;
    resourcesTotal: number;
}

interface OwnProps {
    scope: string;
}

type ResourceOverviewGridProps = StateProps & OwnProps;

const TOTAL = 'total';
const CATEGORY = 'category';

const isTotal = isEqual(TOTAL);

const getFloorMapping = map(({ id: floorId, name }: Floor) => ({
    title: name,
    valueKey: floorId,
    render: ({ id }: { [key: string]: string }) => {
        if (isTotal(id)) {
            return <ResourceGridCell floorId={floorId} isTotal />;
        }
        return <ResourceGridCell floorId={floorId} id={id} />;
    },
}));

const mapping = (floors: Floor[]) => [
    {
        title: <LocalizedText textKey={TextKey.ResourceCategory} />,
        valueKey: CATEGORY,
        render: ({ id }: { [key: string]: string }) => {
            if (isTotal(id)) {
                return (
                    <ResourceGridCellWrapper>
                        <LocalizedText textKey={TextKey.Total} />
                    </ResourceGridCellWrapper>
                );
            }
            return <ResourceOverviewCategory id={id} />;
        },
    },
    ...getFloorMapping(floors),
    {
        title: <LocalizedText textKey={TextKey.Total} />,
        valueKey: TOTAL,
        render: ({ id, total }: { [key: string]: string }) => {
            if (isTotal(id)) {
                return <ResourceGridCellWrapper>{total}</ResourceGridCellWrapper>;
            }
            return <ResourceGridCell id={id} isTotal />;
        },
    },
];

const ResourceOverviewGrid = ({
    orderedCategoryIds,
    floors,
    scope,
    resourcesTotal,
}: ResourceOverviewGridProps): JSX.Element => {
    const source = map((categoryId: string) => ({
        id: categoryId,
    }));

    const total = {
        id: TOTAL,
        total: resourcesTotal,
    };
    // @ts-expect-error $TSFixMeError: Type '({ title: string; valueKey: string; render: ({ id }: { [key: string]: string; }) => Element; } | { title: Element;...
    const tableMapping: TableMapping[] = mapping(floors);
    const tableSource: TableSource[] = [...source(orderedCategoryIds), total];

    return (
        <TableContainerElement>
            <CardTitleElement>
                <LocalizedText textKey={TextKey.SpaceOverview} />
            </CardTitleElement>
            <Card>
                <ResponsiveTable mapping={tableMapping} source={tableSource} />
            </Card>

            <ResourceOverviewActionButtons scope={scope} />

            <ExportConfigurationButtons scope={scope} />
        </TableContainerElement>
    );
};

export default connect(
    createStructuredSelector<{}, StateProps>({
        orderedCategoryIds: getCategoriesOrdering,
        floors: getFloors,
        resources: getResources,
        resourcesTotal: getTotalResourcesCount,
    })
)(ResourceOverviewGrid);
